// src/components/SchoolsTable.js
import React, { useState, useMemo } from 'react';
import { 
  calculatePublicationProblems,
  getSchoolTimeliness,
  hasTimelinessProblems,
  getSchoolCompliance,
  hasErrorsInMenu,
  isDateOutdatedTmRk,
  isDateOutdatedcp,
  extractSchoolNumber,
  getSchoolNumber,
  hasSchoolProblems,
  THRESHOLDS
} from '../utils/calculations';

const SchoolsTable = ({ district, districtData, updateFilters, filters }) => {
  const [sortConfig, setSortConfig] = useState({ column: 'number', direction: 'asc' });

  // ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
  const hasPublicationProblemsValue = (school, data, filters) => {
    const publicationProblems = calculatePublicationProblems(data, filters.month, filters.year);
    return publicationProblems.some(s => s.Код === school.Код);
  };

  const hasAnyProblems = (school) => {
    const allData = {
      dates: districtData.dates,
      errors: districtData.errors,
      publication: districtData.publication,
      compliance: districtData.compliance,
      timeliness: districtData.timeliness
    };
    return hasSchoolProblems(school, allData, filters);
  };

  const getSchoolTableIndicators = (school, data, filters) => {
    const tmStatus = isDateOutdatedTmRk(school.ДатаТМ) ? 'error' : 'success';
    const tmSymbol = isDateOutdatedTmRk(school.ДатаТМ) ? '✗' : '✓';
    const tmText = isDateOutdatedTmRk(school.ДатаТМ) ? 'Неактуально' : 'Актуально';
    
    const kpStatus = isDateOutdatedcp(school.ДатаКП) ? 'error' : 'success';
    const kpSymbol = isDateOutdatedcp(school.ДатаКП) ? '✗' : '✓';
    const kpText = isDateOutdatedcp(school.ДатаКП) ? 'Неактуально' : 'Актуально';
    
    const rkStatus = isDateOutdatedTmRk(school.ДатаРК) ? 'error' : 'success';
    const rkSymbol = isDateOutdatedTmRk(school.ДатаРК) ? '✗' : '✓';
    const rkText = isDateOutdatedTmRk(school.ДатаРК) ? 'Неактуально' : 'Актуально';
    
    const avgCompliance = getSchoolCompliance(school, data.compliance);
    const complianceStatus = avgCompliance >= THRESHOLDS.COMPLIANCE_SCHOOL_TABLE ? 'success' : 'error';
    const complianceSymbol = avgCompliance >= THRESHOLDS.COMPLIANCE_SCHOOL_TABLE ? '✓' : '✗';
    const complianceText = avgCompliance >= THRESHOLDS.COMPLIANCE_SCHOOL_TABLE ? 'Соответствует' : 'Не соответствует';
    
    const hasErrors = hasErrorsInMenu(school, data.errors);
    const errorsStatus = hasErrors ? 'error' : 'success';
    const errorsSymbol = hasErrors ? '✗' : '✓';
    const errorsText = hasErrors ? 'Есть ошибки' : 'Нет ошибок';
    
    const hasPublicationProblems = hasPublicationProblemsValue(school, data, filters);
    const publicationStatus = hasPublicationProblems ? 'error' : 'success';
    const publicationSymbol = hasPublicationProblems ? '✗' : '✓';
    const publicationText = hasPublicationProblems ? 'Нерегулярно' : 'Регулярно';
    
    const timelinessPercentage = getSchoolTimeliness(school, data.timeliness);
    const hasTimelinessIssue = timelinessPercentage !== null && timelinessPercentage < THRESHOLDS.TIMELINESS_GOOD;
    const timelinessStatus = hasTimelinessIssue ? 'error' : 'success';
    const timelinessSymbol = hasTimelinessIssue ? '✗' : '✓';
    const timelinessText = hasTimelinessIssue 
      ? `Низкая (${timelinessPercentage}%)` 
      : (timelinessPercentage ? `Высокая (${timelinessPercentage}%)` : 'Нет данных');
    
    return {
      tm: { status: tmStatus, symbol: tmSymbol, text: tmText },
      kp: { status: kpStatus, symbol: kpSymbol, text: kpText },
      rk: { status: rkStatus, symbol: rkSymbol, text: rkText },
      compliance: { status: complianceStatus, symbol: complianceSymbol, text: complianceText },
      errors: { status: errorsStatus, symbol: errorsSymbol, text: errorsText },
      publication: { status: publicationStatus, symbol: publicationSymbol, text: publicationText },
      timeliness: { status: timelinessStatus, symbol: timelinessSymbol, text: timelinessText }
    };
  };

  // useMemo после объявления всех функций
  const sortedSchools = useMemo(() => {
    const schools = [...districtData.dates];
    
    return schools.sort((a, b) => {
      let valueA, valueB;
      
      switch (sortConfig.column) {
        case 'number':
          valueA = extractSchoolNumber(a.Наименование) || 9999;
          valueB = extractSchoolNumber(b.Наименование) || 9999;
          break;
        case 'name':
          valueA = a.Наименование.toLowerCase();
          valueB = b.Наименование.toLowerCase();
          break;
        case 'tm':
          valueA = isDateOutdatedTmRk(a.ДатаТМ) ? 0 : 1;
          valueB = isDateOutdatedTmRk(b.ДатаТМ) ? 0 : 1;
          break;
        case 'kp':
          valueA = isDateOutdatedcp(a.ДатаКП) ? 0 : 1;
          valueB = isDateOutdatedcp(b.ДатаКП) ? 0 : 1;
          break;
        case 'rk':
          valueA = isDateOutdatedTmRk(a.ДатаРК) ? 0 : 1;
          valueB = isDateOutdatedTmRk(b.ДатаРК) ? 0 : 1;
          break;
        case 'compliance':
          valueA = getSchoolCompliance(a, districtData.compliance);
          valueB = getSchoolCompliance(b, districtData.compliance);
          break;
        case 'errors':
          valueA = hasErrorsInMenu(a, districtData.errors) ? 0 : 1;
          valueB = hasErrorsInMenu(b, districtData.errors) ? 0 : 1;
          break;
        case 'publication':
          valueA = hasPublicationProblemsValue(a, districtData, filters) ? 0 : 1;
          valueB = hasPublicationProblemsValue(b, districtData, filters) ? 0 : 1;
          break;
        case 'timeliness':
          valueA = hasTimelinessProblems(a, districtData.timeliness, THRESHOLDS.TIMELINESS_GOOD) ? 0 : 1;
          valueB = hasTimelinessProblems(b, districtData.timeliness, THRESHOLDS.TIMELINESS_GOOD) ? 0 : 1;
          break;
        default:
          valueA = 0;
          valueB = 0;
      }
      
      if (valueA < valueB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [districtData, sortConfig, filters]);

  const handleSort = (column) => {
    const direction = sortConfig.column === column && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ column, direction });
  };

  const handleSchoolClick = (schoolId) => {
    updateFilters({ school: schoolId });
  };

  const handleExport = () => {
    const csvContent = generateCSV(sortedSchools, districtData, filters);
    downloadCSV(csvContent, `ОУ_района_${district}.csv`);
  };

  const getSortSymbol = (column) => {
    if (sortConfig.column === column) {
      return sortConfig.direction === 'asc' ? '↑' : '↓';
    }
    return '↕';
  };

  const generateCSV = (schools, data, filters) => {
    const BOM = '\uFEFF';
    
    const headers = [
      'Номер ОУ', 'Наименование ОУ', 'Типовое меню', 'Дата ТМ',
      'Календарь питания', 'Дата КП', 'Родительский контроль', 'Дата РК',
      'Соответствие меню', 'Процент соответствия', 'Ошибки в меню',
      'Публикация меню', 'Своевременность', 'Процент своевременности', 'Код ОУ'
    ];
    
    const rows = schools.map(school => {
      const indicators = getSchoolTableIndicators(school, data, filters);
      const schoolNumber = getSchoolNumber(school.Наименование);
      const complianceValue = getSchoolCompliance(school, data.compliance);
      const timelinessPercentage = getSchoolTimeliness(school, data.timeliness);
      
      return [
        schoolNumber, school.Наименование, indicators.tm.text, school.ДатаТМ || 'нет данных',
        indicators.kp.text, school.ДатаКП || 'нет данных', indicators.rk.text, school.ДатаРК || 'нет данных',
        indicators.compliance.text, `${complianceValue.toFixed(1)}%`, indicators.errors.text,
        indicators.publication.text, indicators.timeliness.text,
        timelinessPercentage !== null ? `${timelinessPercentage}%` : 'нет данных', school.Код
      ];
    });
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(';'))
      .join('\r\n');
    
    return BOM + csvContent;
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div id="district-schools-problems">
      <div className="table-header">
        <h4>Образовательные учреждения района: {sortedSchools.length} ОУ</h4>
        <button className="export-btn" onClick={handleExport}>Экспорт в Excel</button>
      </div>
      
      <div className="table-container">
        <table id="schools-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('number')}>№ {getSortSymbol('number')}</th>
              <th onClick={() => handleSort('name')}>ОУ {getSortSymbol('name')}</th>
              <th onClick={() => handleSort('tm')} title="Типовое меню">ТМ {getSortSymbol('tm')}</th>
              <th onClick={() => handleSort('kp')} title="Календарь питания">КП {getSortSymbol('kp')}</th>
              <th onClick={() => handleSort('rk')} title="Родительский контроль">РК {getSortSymbol('rk')}</th>
              <th onClick={() => handleSort('compliance')} title="Соответствие меню">Совп. {getSortSymbol('compliance')}</th>
              <th onClick={() => handleSort('errors')} title="Ошибки в меню">Ош. {getSortSymbol('errors')}</th>
              <th onClick={() => handleSort('publication')} title="Публикация меню">Публ. {getSortSymbol('publication')}</th>
              <th onClick={() => handleSort('timeliness')} title="Своевременность">Своевр. {getSortSymbol('timeliness')}</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {sortedSchools.map(school => {
              const schoolNumber = getSchoolNumber(school.Наименование);
              const indicators = getSchoolTableIndicators(school, districtData, filters);
              const hasProblems = hasAnyProblems(school);
              const rowClass = hasProblems ? 'problem-row' : '';
              
              return (
                <tr key={school.Код} className={rowClass}>
                  <td className="school-number-cell">{schoolNumber}</td>
                  <td>
                    <a 
                      href={`https://monitoring.cemon.ru/#/profile?id=${school.Код}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="school-link"
                    >
                      {school.Наименование}
                    </a>
                  </td>
                  <td className={`status-cell ${indicators.tm.status}`} title={indicators.tm.text}>
                    {indicators.tm.symbol}
                  </td>
                  <td className={`status-cell ${indicators.kp.status}`} title={indicators.kp.text}>
                    {indicators.kp.symbol}
                  </td>
                  <td className={`status-cell ${indicators.rk.status}`} title={indicators.rk.text}>
                    {indicators.rk.symbol}
                  </td>
                  <td className={`status-cell ${indicators.compliance.status}`} title={indicators.compliance.text}>
                    {indicators.compliance.symbol}
                  </td>
                  <td className={`status-cell ${indicators.errors.status}`} title={indicators.errors.text}>
                    {indicators.errors.symbol}
                  </td>
                  <td className={`status-cell ${indicators.publication.status}`} title={indicators.publication.text}>
                    {indicators.publication.symbol}
                  </td>
                  <td className={`status-cell ${indicators.timeliness.status}`} title={indicators.timeliness.text}>
                    {indicators.timeliness.symbol}
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="view-details-btn"
                      onClick={() => handleSchoolClick(school.Код)}
                      title="Подробная информация"
                    >
                      Детали
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SchoolsTable;